import { MapPin, MessageCircle, Mail } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, RevealLines } from "@/components/Reveal";
import { QuoteForm } from "@/components/QuoteForm";
import { RegionMap } from "@/components/RegionMap";
import { FaqSection } from "@/components/FaqSection";
import { ATMO, SITE, waLink } from "@/config/site";

export default function Kontakt() {
  return (
    <>
      <Seo
        title="Kontakt & kostenlose Beratung | Hanse Garten Bremen"
        description="Kontakt zu Hanse Garten Bremen: kostenlose Beratung für Ihr Gartenprojekt in Bremen. Anfrage per Formular oder direkt über WhatsApp."
        path="/kontakt"
        image={ATMO.licht}
      />
      <PageHero
        eyebrow="Kontakt"
        titleLines={["Lassen wir etwas", "wachsen."]}
        text="Erzählen Sie uns von Ihrem Gartenprojekt. Wir beraten Sie persönlich und besprechen gemeinsam die nächsten Schritte."
        image={ATMO.licht}
        breadcrumb="Kontakt"
      />

      <section className="bg-[#F3EDE2] py-24 md:py-36">
        <div className="mx-auto max-w-[1480px] px-5 md:px-10">
          <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
            <div>
              <p className="eyebrow">Direkter Weg</p>
              <RevealLines
                lines={["Sprechen wir", "über Ihren Garten."]}
                as="h2"
                className="display-xl mt-5 text-[9vw] leading-[0.94] text-[#17251C] md:text-[2.9vw]"
              />

              <Reveal delay={0.12}>
                <div className="mt-10 space-y-3">
                  <a
                    href={waLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="kontakt-whatsapp-card"
                    className="group flex items-start gap-4 rounded-[1.6rem] border border-[#17251C]/14 bg-[#DCCDB4]/40 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#B4623C]"
                  >
                    <MessageCircle className="mt-1 shrink-0 text-[#B4623C]" size={19} />
                    <span>
                      <span className="eyebrow block">WhatsApp</span>
                      <span className="display-xl mt-1.5 block text-xl text-[#17251C]">{SITE.whatsappNumber}</span>
                      <span className="mt-1.5 block text-sm text-[#6E7A69]">
                        Schnellster Weg – gerne auch mit Fotos Ihres Gartens.
                      </span>
                    </span>
                  </a>

                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="kontakt-email-card"
                    className="group flex items-start gap-4 rounded-[1.6rem] border border-[#17251C]/14 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#B4623C]"
                  >
                    <Mail className="mt-1 shrink-0 text-[#B4623C]" size={19} />
                    <span>
                      <span className="eyebrow block">E-Mail</span>
                      <span className="mt-1.5 block break-all font-display text-lg font-semibold tracking-tight text-[#17251C]">
                        {SITE.email}
                      </span>
                      <span className="mt-1.5 block text-sm text-[#6E7A69]">
                        Schreiben Sie uns gerne mit Fotos und Details zu Ihrem Garten.
                      </span>
                    </span>
                  </a>

                  <div className="flex items-start gap-4 rounded-[1.6rem] border border-[#17251C]/14 p-6">
                    <MapPin className="mt-1 shrink-0 text-[#B4623C]" size={19} />
                    <div>
                      <span className="eyebrow block">Adresse</span>
                      <p className="display-xl mt-1.5 text-lg text-[#17251C]">{SITE.name}</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#6E7A69]">
                        Inhaber: {SITE.owner} · {SITE.legalForm}
                        <br />
                        {SITE.street}
                        <br />
                        {SITE.zipCity}, {SITE.country}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.2} className="mt-10">
                <RegionMap />
              </Reveal>
            </div>

            <div id="anfrage">
              <p className="eyebrow">Anfrage</p>
              <RevealLines
                lines={["Kostenlose Beratung."]}
                as="h2"
                className="display-xl mt-5 text-[9vw] leading-[0.94] text-[#17251C] md:text-[3vw]"
              />
              <div className="mt-10">
                <QuoteForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#DCCDB4]/30 py-24 md:py-32">
        <div className="mx-auto grid max-w-[1200px] gap-12 px-5 md:px-10 lg:grid-cols-[0.75fr_1.25fr]">
          <div>
            <p className="eyebrow">FAQ</p>
            <RevealLines
              lines={["Häufige", "Fragen."]}
              as="h2"
              className="display-xl mt-5 text-[10vw] leading-[0.92] text-[#17251C] md:text-[3.4vw]"
            />
          </div>
          <FaqSection />
        </div>
      </section>
    </>
  );
}
